import { Box, Card, Checkbox, CircularProgress, FormControlLabel, Grid, IconButton, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { fCoins } from "src/utils/formatNumber";
import { fDate } from "src/utils/formatTime";
import Iconify from "../iconify/Iconify";

const UserProperty = ({ name, value, isEditable, onValueChange, onEdit, icon, minWidth = "100px", isLoading = false }) => {
	const [localValue, setLocalValue] = useState(value);
	const [editing, setEditing] = useState(false);

	const handleInputChange = (e) => {
		setLocalValue(e.target.value);
		if (onValueChange) {
			onValueChange(e.target.value);
		}
	};

	const handleEditClick = () => {
		if (editing) {
			if (onEdit) {
				onEdit(localValue);
			}
		}
		setEditing(!editing);
	};

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 2 }}>
			<Typography variant="caption" sx={{ fontWeight: "bold", minWidth: minWidth, fontSize: "0.875rem" }}>
				{name}
			</Typography>
			<TextField
				value={localValue}
				onChange={handleInputChange}
				disabled={!editing}
				variant="outlined"
				size="small"
				InputProps={
					icon
						? {
								startAdornment: (
									<Box
										component="img"
										src={icon}
										sx={{
											width: 20,
											height: 20,
											marginRight: 1,
										}}
										alt=""
									/>
								),
							}
						: {}
				}
				sx={{
					flexGrow: 1,
					"& .MuiInputBase-input.Mui-disabled": {
						WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
					},
				}}
			/>
			{isEditable && (
				<IconButton onClick={handleEditClick} size="small" disabled={isLoading}>
					<Iconify icon={isLoading ? "eos-icons:loading" : editing ? "eva:save-outline" : "eva:edit-outline"} />
				</IconButton>
			)}
		</Box>
	);
};

export default function ProfileUserData({ user, onSaveField }) {
	const [loadingFields, setLoadingFields] = useState({});
	const calculateAccountAge = (createdAt) => {
		if (!createdAt) return "N/A";

		const now = new Date();
		const createdDate = new Date(createdAt);
		const diffInMs = now - createdDate;
		const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

		return `${diffInDays} days`;
	};

	const handleValueChange = (field, value) => {
		console.log(`${field} changed to:`, value);
	};

	const handleEdit = (field, value) => {
		if (onSaveField) {
			setLoadingFields((prev) => ({ ...prev, [field]: true }));
			try {
				const result = onSaveField(field, value);
				if (result && typeof result.finally === "function") {
					result.finally(() => {
						setLoadingFields((prev) => ({ ...prev, [field]: false }));
					});
				} else {
					// If onSaveField doesn't return a promise, clear loading after a short delay
					setTimeout(() => {
						setLoadingFields((prev) => ({ ...prev, [field]: false }));
					}, 500);
				}
			} catch (error) {
				setLoadingFields((prev) => ({ ...prev, [field]: false }));
			}
		}
	};

	const handleChangeEnabled = (isEnabled) => {
		handleValueChange("isEnabled", isEnabled);
		if (onSaveField) {
			setLoadingFields((prev) => ({ ...prev, isEnabled: true }));
			try {
				const result = onSaveField("isEnabled", isEnabled);
				if (result && typeof result.finally === "function") {
					result.finally(() => {
						setLoadingFields((prev) => ({ ...prev, isEnabled: false }));
					});
				} else {
					// If onSaveField doesn't return a promise, clear loading after a short delay
					setTimeout(() => {
						setLoadingFields((prev) => ({ ...prev, isEnabled: false }));
					}, 500);
				}
			} catch (error) {
				setLoadingFields((prev) => ({ ...prev, isEnabled: false }));
			}
		}
	};

	return (
		<Card sx={{ mb: 2, p: 2 }}>
			<Grid container spacing={3}>
				<Grid item xs={4}>
					<UserProperty name="User ID" value={user.userId} isEditable={false} onValueChange={() => {}} onEdit={() => {}} />
					<UserProperty
						name="Username"
						value={user.username}
						isEditable={true}
						onValueChange={(value) => handleValueChange("username", value)}
						onEdit={(value) => handleEdit("username", value)}
						isLoading={loadingFields.username}
					/>
					<UserProperty
						name="First Name"
						value={user.firstName}
						isEditable={true}
						onValueChange={(value) => handleValueChange("firstName", value)}
						onEdit={(value) => handleEdit("firstName", value)}
						isLoading={loadingFields.firstName}
					/>
					<UserProperty
						name="Last Name"
						value={user.lastName}
						isEditable={true}
						onValueChange={(value) => handleValueChange("lastName", value)}
						onEdit={(value) => handleEdit("lastName", value)}
						isLoading={loadingFields.lastName}
					/>
					<UserProperty name="Birth Date" value={fDate(user.birthDate) ?? "N/A"} isEditable={false} />
				</Grid>
				<Grid item xs={4}>
					<UserProperty
						name="Email"
						value={user.email}
						isEditable={true}
						onValueChange={(value) => handleValueChange("email", value)}
						onEdit={(value) => handleEdit("email", value)}
						isLoading={loadingFields.email}
					/>
					<UserProperty
						name="Addit. Email"
						value={user.additionalEmail}
						isEditable={true}
						onValueChange={(value) => handleValueChange("additionalEmail", value)}
						onEdit={(value) => handleEdit("additionalEmail", value)}
						isLoading={loadingFields.additionalEmail}
					/>
					<UserProperty name="Age of Account" value={calculateAccountAge(user.createdAt)} isEditable={false} />
					<UserProperty
						name="Coin Balance"
						value={fCoins(user.coinsBalance)}
						isEditable={false}
						onValueChange={() => {}}
						onEdit={() => {}}
						icon="/assets/icons/ufcoin.svg"
					/>
					<UserProperty name="Device ID" value={user.deviceId} isEditable={false} />
				</Grid>
				<Grid item xs={4}>
					<UserProperty name="Referral Code" value={user.referralCode} isEditable={false} />
					<UserProperty name="Referrals" value={user.referrals} isEditable={false} />
					<UserProperty
						name="Days Until First Purchase"
						value={user.daysUntilFirstPurchase}
						isEditable={false}
						onValueChange={() => {}}
						onEdit={() => {}}
					/>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<UserProperty
								name="Pts"
								value={fCoins(user?.xpDetails?.totalRewards ?? 0)}
								isEditable={false}
								icon="/assets/icons/xp-level-badge.svg"
								minWidth="20px"
							/>
						</Grid>
						<Grid item xs={6}>
							<FormControlLabel
								control={
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<Checkbox
											checked={user.isEnabled}
											onChange={(event) => handleChangeEnabled(event.target.checked)}
											id="isEnabled"
											disabled={loadingFields.isEnabled}
										/>
										{loadingFields.isEnabled && <CircularProgress size={16} sx={{ ml: 1 }} />}
									</Box>
								}
								label="Is Enabled"
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Card>
	);
}
